import { useSDKActionWithDeps } from "@sdk/sdk.hooks";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  InfoCircledIcon,
  CheckCircledIcon,
  ExclamationTriangleIcon,
} from "@radix-ui/react-icons";
import { Globe } from "lucide-react";
import { useMemo, useState } from "react";
import { useDispatch } from "react-redux";
import { useBrandById, useBrands } from "@/hooks/use-brands";
import { setBrand } from "@/store/modules/brands/brands.slice";
import { WorkspaceSectionHeader } from "../workspace-section-header";

export const QuotesDomainSection = ({ brandId }: { brandId: string }) => {
  return (
    <div className="space-y-6 px-4 pb-6">
      <WorkspaceSectionHeader
        icon={Globe}
        title="Quotes Domain"
        description="Configure the custom domain used for sharing quotes with customers."
      />
      <QuotesDomainEditor brandId={brandId} />
    </div>
  );
};

const QuotesDomainEditor = ({ brandId }: { brandId: string }) => {
  const brand = useBrandById(brandId);
  const customDomainConfig = brand?.customPagesDomainConfig;
  const dispatch = useDispatch();
  const { retry: retryBrands } = useBrands();

  const [customDomainInput, setCustomDomainInput] = useState("");

  const { doAction: setCustomDomain, isProcessing: isSettingCustomDomain } =
    useSDKActionWithDeps(
      () => ({
        action: (SDK) => (domain: string) =>
          SDK.brands.setPagesCustomDomain(brandId, { domain }).then(
            (updated) => {
              dispatch(setBrand(updated));
              retryBrands();
              return updated;
            }
          ),
        successMessage: "Done",
        failureMessage: "Something went wrong",
        actionDependencies: [],
      }),
      [brandId, dispatch, retryBrands]
    );

  const { doAction: removeCustomDomain, isProcessing: idRemoveProcessing } =
    useSDKActionWithDeps(
      () => ({
        action: (SDK) => () =>
          SDK.brands.removePagesCustomDomain(brandId).then((updated) => {
            dispatch(setBrand(updated));
            retryBrands();
            return updated;
          }),
        successMessage: "Domain removed",
        failureMessage: "Something went wrong",
        actionDependencies: [],
      }),
      [brandId, dispatch, retryBrands]
    );

  const { doAction: refreshValidityCustomDomain, isProcessing: isRefreshing } =
    useSDKActionWithDeps(
      () => ({
        action: (SDK) => () =>
          SDK.brands.verifyPagesCustomDomain(brandId).then((updated) => {
            dispatch(setBrand(updated));
            retryBrands();
            return updated;
          }),
        //   successMessage: "Done",
        failureMessage: "Something went wrong",
        actionDependencies: [],
      }),
      [brandId, dispatch, retryBrands]
    );

  const columns = useMemo(() => {
    return [
      {
        title: "Type",
        dataIndex: "type",
      },
      {
        title: "Hostname",
        dataIndex: "domain",
      },
      {
        title: "Enter This Value",
        dataIndex: "value",
        render: (value: string) => (
          <div
            className="p-4 bg-gray-100  dark:bg-gray-700 rounded-lg mode_transition"
            style={{ maxWidth: 200 }}
          >
            {value}
          </div>
        ),
      },
      {
        title: "Status",
        dataIndex: "reason",
      },
    ];
  }, []);

  const customDomainDNSConfig = useMemo(() => {
    return [
      {
        type: "CNAME",
        domain: customDomainConfig?.domain,
        value: "cname.vercel-dns.com",
      },
    ];
  }, [customDomainConfig?.domain]);

  // Remove unused columns definition since we're using direct table rendering
  const renderTable = (data: any[]) => (
    <Table>
      <TableHeader>
        <TableRow>
          {["Type", "Hostname", "Enter This Value", "Status"].map((header) => (
            <TableHead key={header}>{header}</TableHead>
          ))}
        </TableRow>
      </TableHeader>
      <TableBody>
        {data.map((row) => (
          <TableRow key={row.value}>
            <TableCell>{row.type}</TableCell>
            <TableCell>{row.domain}</TableCell>
            <TableCell>
              <div className="p-4 bg-muted rounded-lg max-w-[200px]">
                <p className="text-sm text-muted-foreground break-all">
                  {row.value}
                </p>
              </div>
            </TableCell>
            <TableCell>{row.reason}</TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );

  if (customDomainConfig?.domain) {
    if (customDomainConfig?.isVerified) {
      if (customDomainConfig.isConfigured) {
        return (
          <>
            <Alert
              variant="default"
              className="bg-green-50 dark:bg-green-900/20"
            >
              <CheckCircledIcon className="h-4 w-4" />
              <AlertDescription>
                You have successfully setup your custom domain
              </AlertDescription>
            </Alert>
            <div className="my-4">
              Custom Domain : {customDomainConfig.domain}
            </div>
            <Button
              variant="destructive"
              onClick={() => removeCustomDomain()}
              disabled={idRemoveProcessing}
            >
              {idRemoveProcessing ? "Removing..." : "Remove Domain"}
            </Button>
          </>
        );
      }
      return (
        <>
          <Alert variant="destructive">
            <ExclamationTriangleIcon className="h-4 w-4" />
            <AlertDescription>
              Domain has been added. However it is not configured yet
            </AlertDescription>
          </Alert>
          <div className="my-4">
            Custom Domain : {customDomainConfig.domain}
          </div>

          <div className="mt-4">
            Please set the following CNAME record on domain DNS settings
          </div>
          {renderTable(customDomainDNSConfig)}
          <div className="mt-4 flex flex-row gap-4 items-center">
            <Button
              variant="destructive"
              onClick={() => removeCustomDomain()}
              disabled={idRemoveProcessing}
            >
              {idRemoveProcessing ? "Removing..." : "Remove Domain"}
            </Button>
            <Button
              onClick={() => refreshValidityCustomDomain()}
              disabled={isRefreshing}
            >
              {isRefreshing ? "Validating..." : "Validate"}
            </Button>
          </div>
        </>
      );
    }
    return (
      <div className="flex flex-col gap-4">
        <Alert variant="default">
          <ExclamationTriangleIcon className="h-4 w-4" />
          <AlertDescription>Please Complete the domain setup</AlertDescription>
        </Alert>

        <div>Please set the following TXT record on domain DNS settings</div>

        {renderTable(customDomainConfig.verification || [])}

        <div className="flex flex-row gap-4">
          <Button
            onClick={() => refreshValidityCustomDomain()}
            disabled={isRefreshing}
          >
            {isRefreshing ? (
              <>
                <span className="animate-spin mr-2">⏳</span>
                Validating...
              </>
            ) : (
              "Validate"
            )}
          </Button>
          <Button
            variant="destructive"
            onClick={() => removeCustomDomain()}
            disabled={idRemoveProcessing}
          >
            {idRemoveProcessing ? (
              <>
                <span className="animate-spin mr-2">⏳</span>
                Removing...
              </>
            ) : (
              "Remove Domain"
            )}
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-4">
      <Alert variant="default">
        <InfoCircledIcon className="h-4 w-4" />
        <AlertDescription>
          You have not setup a custom domain yet
        </AlertDescription>
      </Alert>

      <div className="flex flex-row gap-4">
        <Input
          value={customDomainInput}
          onChange={(e) => setCustomDomainInput(e.target.value)}
          className="flex-1"
          placeholder="Your Domain"
        />
        <Button
          onClick={() => setCustomDomain(customDomainInput)}
          disabled={isSettingCustomDomain}
        >
          {isSettingCustomDomain ? (
            <>
              <span className="animate-spin mr-2">⏳</span>
              Setting up...
            </>
          ) : (
            "Setup"
          )}
        </Button>
      </div>
    </div>
  );
};
